import { notifyError } from 'components/Notification/Notification';

export const REQUIRED_HIERARCHY_FIELD = {
  'DIVNRA': 'parent',
  'OPASN': 'group',
  'OPEXC': 'group',
  'OPEXP': 'group',
  'OPTRD': 'group',
  'OPCSH': 'group',
};

const REQUIRED_ANNOTATION_KEYS = {
  // '<entryType>': [...annotationKeys], // Required for all entry types
  // '<entryType>:<entrySubType>': [...annotationKeys], // Required for specific entry sub types

  'VOF': ['position_date'],
  'VOF:VDST': ['source_symbol', 'source_cusip', 'source_qty', 'rate', 'new_cusip', 'new_symbol', 'new_qty'],
  'VOF:VEXH': ['source_symbol', 'source_cusip'],
  'VOF:VRGT': ['source_symbol', 'source_cusip'],
  'VOF:VTND': ['source_symbol', 'source_cusip'],
  'VOF:VWRT': ['source_symbol', 'source_cusip'],

  'OPASN': ['underlying_symbol', 'contract_symbol', 'underlying_cusip'],
  'OPEXC': ['underlying_symbol', 'contract_symbol', 'underlying_cusip'],
  'OPEXP': ['underlying_symbol', 'contract_symbol', 'underlying_cusip'],
  'OPTRD': ['underlying_symbol', 'contract_symbol', 'underlying_cusip'],
  'OPCSH': ['underlying_symbol', 'contract_symbol', 'underlying_cusip'],

  'FEE:ADR': ['symbol', 'rate', 'record_date', 'qty'],
};

export const getRequiredAnnotationKeys = ({ entryType, entrySubType }) => {
  return [
    ...(REQUIRED_ANNOTATION_KEYS[entryType] || []),
    ...((entrySubType && REQUIRED_ANNOTATION_KEYS[`${entryType}:${entrySubType}`]) || []),
  ];
};

export function affectsShortLeg(metadataValue, qty) {
  const parsedQty = Number(qty);
  if (!Number.isFinite(parsedQty) || parsedQty === 0 || !metadataValue) {
    return false;
  }

  let metadata = metadataValue;
  if (typeof metadataValue === 'string') {
    try {
      metadata = JSON.parse(metadataValue);
    } catch {
      return false;
    }
  }

  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return false;
  }

  const toOpen = metadata.to_open;
  if (typeof toOpen !== 'boolean') {
    return false;
  }

  return (toOpen && parsedQty < 0) || (!toOpen && parsedQty > 0);
}

export function getEntryTypeMetadataErrors(entryType, entrySubType, metadataStr) {
  const requiredHierarchyField = REQUIRED_HIERARCHY_FIELD[entryType];
  const requiredAnnotationKeys = getRequiredAnnotationKeys({ entryType, entrySubType });

  let parsed = {};
  if (metadataStr) {
    try {
      parsed = JSON.parse(metadataStr);
    } catch (e) {
      return ['Metadata is not valid JSON: ' + e.message];
    }
  }

  if (requiredHierarchyField === 'parent') {
    const parent = parsed?.hierarchy?.parent;
    if (!parent) {
      return [`${entryType} requires metadata.hierarchy.parent (parent_id).`];
    }
    return [];
  }

  if (requiredHierarchyField === 'group') {
    const group = parsed?.hierarchy?.group;
    if (!group) {
      return [`${entryType} requires metadata.hierarchy.group (group_id).`];
    }
  }

  const annotation = parsed?.annotation || {};
  const annotationMap =
    annotation && typeof annotation === 'object' && !Array.isArray(annotation)
      ? annotation
      : Object.fromEntries(
          (Array.isArray(annotation) ? annotation : []).filter(
            (e) => Array.isArray(e) && e.length >= 2
          )
        );
  const missing = requiredAnnotationKeys.filter((k) => {
    const v = annotationMap[k];
    return v === undefined || v === null || v === '';
  });
  if (missing.length > 0) {
    return [
      `${entryType}${
        entrySubType ? ` (${entrySubType})` : ''
      } requires annotation keys: ${missing.join(', ')}.`,
    ];
  }
  return [];
}

// Returns true when valid; false (after notifyError) when a required field is missing.
export function validateEntryTypeMetadata(entryType, entrySubType, metadataStr) {
  const [error] = getEntryTypeMetadataErrors(entryType, entrySubType, metadataStr);
  if (error) {
    notifyError(error);
    return false;
  }
  return true;
}
